import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { annoncesAPI, refAPI } from '../services/api';
import AnnonceCard from '../components/AnnonceCard';
import './ListeAnnonces.css';

export default function ListeAnnonces() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [annonces, setAnnonces] = useState([]);
  const [pagination, setPagination] = useState({});
  const [territoires, setTerritoires] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const filters = {
    q:           searchParams.get('q') || '',
    territoire:  searchParams.get('territoire') || '',
    categorie:   searchParams.get('categorie') || '',
    prix_min:    searchParams.get('prix_min') || '',
    prix_max:    searchParams.get('prix_max') || '',
    etat:        searchParams.get('etat') || '',
    tri:         searchParams.get('tri') || 'recent',
    page:        searchParams.get('page') || '1',
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await annoncesAPI.liste({ ...filters, limite: 24 });
      setAnnonces(res.data.annonces);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    refAPI.territoires().then(r => setTerritoires(r.data));
    refAPI.categories().then(r => setCategories(r.data));
  }, []);

  const nbFiltres = [filters.territoire, filters.categorie, filters.prix_min, filters.prix_max, filters.etat]
    .filter(Boolean).length;

  return (
    <div className="liste-page">
      <div className="container liste-layout">

        {/* ── Sidebar filtres ─────────────────────────────────── */}
        <aside className="filtres-sidebar">
          <div className="filtres-header">
            <h3>Filtres {nbFiltres > 0 && <span className="filtres-badge">{nbFiltres}</span>}</h3>
            {nbFiltres > 0 && (
              <button className="filtres-reset" onClick={() => setSearchParams(new URLSearchParams())}>
                Réinitialiser
              </button>
            )}
          </div>

          {/* Territoire */}
          <div className="filtre-group">
            <label className="filtre-label">Territoire</label>
            <select
              className="form-input"
              value={filters.territoire}
              onChange={e => setFilter('territoire', e.target.value)}
            >
              <option value="">Tous</option>
              {territoires.map(t => (
                <option key={t.id} value={t.slug}>{t.nom}</option>
              ))}
            </select>
          </div>

          {/* Catégorie */}
          <div className="filtre-group">
            <label className="filtre-label">Catégorie</label>
            <select
              className="form-input"
              value={filters.categorie}
              onChange={e => setFilter('categorie', e.target.value)}
            >
              <option value="">Toutes</option>
              {categories.map(c => (
                <option key={c.id} value={c.slug}>{c.nom}</option>
              ))}
            </select>
          </div>

          {/* Prix */}
          <div className="filtre-group">
            <label className="filtre-label">Prix (€)</label>
            <div className="prix-range">
              <input
                type="number" placeholder="Min"
                className="form-input prix-input"
                value={filters.prix_min}
                onChange={e => setFilter('prix_min', e.target.value)}
              />
              <span className="prix-sep">—</span>
              <input
                type="number" placeholder="Max"
                className="form-input prix-input"
                value={filters.prix_max}
                onChange={e => setFilter('prix_max', e.target.value)}
              />
            </div>
          </div>

          {/* État */}
          <div className="filtre-group">
            <label className="filtre-label">État</label>
            <div className="etat-options">
              {[
                { v: '', l: 'Tous' },
                { v: 'neuf', l: 'Neuf' },
                { v: 'tres_bon', l: 'Très bon état' },
                { v: 'bon', l: 'Bon état' },
                { v: 'acceptable', l: 'Acceptable' },
              ].map(opt => (
                <button
                  key={opt.v}
                  className={`etat-btn ${filters.etat === opt.v ? 'etat-btn--active' : ''}`}
                  onClick={() => setFilter('etat', opt.v)}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* ── Contenu principal ───────────────────────────────── */}
        <main className="liste-main">

          {/* Barre résultats */}
          <div className="liste-topbar">
            <div className="liste-info">
              {filters.q && <span>Résultats pour <strong>"{filters.q}"</strong> — </span>}
              <span>{pagination.total || 0} annonce{pagination.total !== 1 ? 's' : ''}</span>
            </div>
            <select
              className="tri-select"
              value={filters.tri}
              onChange={e => setFilter('tri', e.target.value)}
            >
              <option value="recent">Plus récentes</option>
              <option value="prix_asc">Prix croissant</option>
              <option value="prix_desc">Prix décroissant</option>
              <option value="vues">Plus vues</option>
            </select>
          </div>

          {/* Grille */}
          {loading ? (
            <div className="loader"><div className="loader-spin" /></div>
          ) : annonces.length === 0 ? (
            <div className="liste-empty">
              <span className="empty-icon">🔍</span>
              <h3>Aucune annonce trouvée</h3>
              <p>Essayez d'élargir vos critères de recherche</p>
              <Link to="/annonces/nouveau" className="btn btn-primary" style={{marginTop: 16}}>
                Déposer la première annonce
              </Link>
            </div>
          ) : (
            <>
              <div className="annonces-grid">
                {annonces.map(a => <AnnonceCard key={a.id} annonce={a} />)}
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="pagination">
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      className={`page-btn ${parseInt(filters.page) === p ? 'page-btn--active' : ''}`}
                      onClick={() => setFilter('page', String(p))}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
